import { getAdvertisementApiRoute } from "../utils/api.utils";

export const ADVERTISEMENT_SELECTION_TIMEOUT_MS = 8000;
export const ADVERTISEMENT_MEDIA_START_TIMEOUT_MS = 12000;

const TRACKING_EVENTS = [
  "creative_view",
  "start",
  "first_quartile",
  "midpoint",
  "third_quartile",
  "complete",
  "mute",
  "unmute",
  "pause",
  "rewind",
  "resume",
  "skip",
  "expand",
  "collapse",
  "impression",
  "video_click",
];

function isKnownTrackingEvent(eventName) {
  return TRACKING_EVENTS.indexOf(eventName) !== -1;
}

export async function loadAdvertisement(contentKey, campaignKey, options) {
  if (!contentKey) return null;

  const settings = options || {};
  const timeoutMs =
    typeof settings.timeoutMs === "number"
      ? settings.timeoutMs
      : ADVERTISEMENT_SELECTION_TIMEOUT_MS;
  const fetchImpl =
    settings.fetchImpl ||
    (typeof fetch === "function" ? fetch : null);
  if (!fetchImpl) return null;
  const query = new URLSearchParams({ contentKey: contentKey });
  if (campaignKey) {
    query.set("campaignKey", campaignKey);
  }

  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timedOut = {};
  let timeoutId;

  try {
    const requestOptions = {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
    };
    if (controller) {
      requestOptions.signal = controller.signal;
    }

    const response = await Promise.race([
      fetchImpl(
        getAdvertisementApiRoute(
          "campaign/gen-active-vast-json?" + query.toString()
        ),
        requestOptions
      ),
      new Promise(function (resolve) {
        timeoutId = setTimeout(function () {
          if (controller) controller.abort();
          resolve(timedOut);
        }, timeoutMs);
      }),
    ]);

    if (response === timedOut) return null;

    if (response.status === 204 || !response.ok) {
      return null;
    }

    const body = await response.json();
    const vast = body && Object.prototype.hasOwnProperty.call(body, "data")
      ? body.data
      : null;

    return isVastJson(vast) ? vast : null;
  } catch (error) {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function isVastJson(value) {
  return Boolean(
    value &&
      value.ad &&
      value.ad.id &&
      value.ad.inline &&
      Array.isArray(value.ad.inline.creatives)
  );
}

export function getVastCreatives(vast) {
  if (!isVastJson(vast)) return [];
  return vast.ad.inline.creatives.filter(function (creative) {
    return Boolean(
      creative &&
        creative.id &&
        creative.linear &&
        Array.isArray(creative.linear.mediaFiles)
    );
  });
}

export function isHlsMedia(media) {
  if (!media) return false;

  const type = media.type
    ? String(media.type).split(";")[0].trim().toLowerCase()
    : "";
  const url = media.url ? String(media.url).toLowerCase() : "";

  return (
    type.indexOf("mpegurl") !== -1 ||
    type.indexOf("m3u8") !== -1 ||
    url.indexOf(".m3u8") !== -1
  );
}

function isPlayableMedia(media) {
  if (!media || !isSafeHttpUrl(media.url)) {
    return false;
  }

  const delivery = media.delivery
    ? String(media.delivery).toLowerCase()
    : "";
  if (isHlsMedia(media)) {
    return (
      !delivery ||
      delivery === "streaming" ||
      delivery === "progressive"
    );
  }

  if (delivery !== "progressive") return false;

  const type = media.type
    ? String(media.type).split(";")[0].trim().toLowerCase()
    : "";
  return !type || type === "video/mp4";
}

export function selectMediaFiles(creative, randomFn) {
  if (!creative || !creative.linear || !Array.isArray(creative.linear.mediaFiles)) {
    return [];
  }

  const playable = creative.linear.mediaFiles.filter(isPlayableMedia);
  const capacityValue = Number(creative.extCapacity);
  const capacity =
    Number.isInteger(capacityValue) && capacityValue > 0
      ? Math.min(capacityValue, playable.length)
      : playable.length;
  const selected = playable.slice();

  if (creative.extRandomMedia) {
    const getRandom = randomFn || Math.random;
    for (let index = selected.length - 1; index > 0; index -= 1) {
      const other = Math.floor(getRandom() * (index + 1));
      const current = selected[index];
      selected[index] = selected[other];
      selected[other] = current;
    }
  }

  return selected.slice(0, capacity);
}

export function trackingUrl(creative, eventName) {
  if (
    !creative ||
    !creative.linear ||
    !Array.isArray(creative.linear.trackingEvents) ||
    !isKnownTrackingEvent(eventName)
  ) {
    return undefined;
  }

  const tracking = creative.linear.trackingEvents.find(function (item) {
    return item && item.event === eventName;
  });
  return tracking && tracking.url ? tracking.url : undefined;
}

export function fireTracking(url, options) {
  if (!url) return Promise.resolve(false);

  const settings = options || {};
  const fetchImpl =
    settings.fetchImpl ||
    (typeof fetch === "function" ? fetch : null);
  if (!fetchImpl) return Promise.resolve(false);

  return fetchImpl(url, {
    method: "GET",
    credentials: "omit",
    keepalive: true,
    cache: "no-store",
  })
    .then(function () {
      return true;
    })
    .catch(function () {
      return false;
    });
}

export function fireTrackingOnce(
  sessionFired,
  vast,
  creative,
  eventName,
  explicitUrl,
  options
) {
  if (
    !sessionFired ||
    !isVastJson(vast) ||
    !creative ||
    !creative.id ||
    !isKnownTrackingEvent(eventName)
  ) {
    return false;
  }

  const url = explicitUrl || trackingUrl(creative, eventName);
  if (!url) return false;

  const key =
    eventName === "impression"
      ? vast.ad.id + ":impression"
      : vast.ad.id + ":" + creative.id + ":" + eventName;

  if (sessionFired.has(key)) return false;
  sessionFired.add(key);
  void fireTracking(url, options);
  return true;
}

export function getCreativeTriggerTime(creative, duration) {
  if (!creative) return null;

  const customTime = Number(creative.extCustomTime);
  if (
    creative.extCustomTime !== undefined &&
    creative.extCustomTime !== null &&
    creative.extCustomTime !== "" &&
    Number.isFinite(customTime) &&
    customTime >= 0
  ) {
    return customTime;
  }

  if (creative.creativeSlot === "pre-roll") return 0;
  if (!Number.isFinite(duration) || duration <= 0) return null;
  if (creative.creativeSlot === "first-quartile") return duration * 0.25;
  if (creative.creativeSlot === "mid-roll") return duration * 0.5;
  if (creative.creativeSlot === "third-quartile") return duration * 0.75;
  return null;
}

export function getDueCreatives(creatives, currentTime, duration, scheduledIds) {
  const scheduled = scheduledIds || {};
  if (!Array.isArray(creatives)) return [];

  return creatives
    .map(function (creative, responseIndex) {
      return {
        creative: creative,
        responseIndex: responseIndex,
        triggerTime: getCreativeTriggerTime(creative, duration),
      };
    })
    .filter(function (item) {
      return Boolean(
        item.creative &&
          !scheduled[item.creative.id] &&
          item.triggerTime !== null &&
          currentTime >= item.triggerTime
      );
    })
    .sort(function (left, right) {
      return (
        left.triggerTime - right.triggerTime ||
        left.responseIndex - right.responseIndex
      );
    })
    .map(function (item) {
      return item.creative;
    });
}

export function isSafeHttpUrl(value) {
  if (!value || typeof value !== "string") return false;

  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch (error) {
    return false;
  }
}

export function safeActionColor(value) {
  if (!value || typeof value !== "string") return undefined;
  if (
    typeof CSS !== "undefined" &&
    CSS.supports &&
    CSS.supports("color", value)
  ) {
    return value;
  }
  return undefined;
}

export function openAdvertiserTarget(value, openImpl) {
  if (!isSafeHttpUrl(value)) return false;

  if (openImpl) {
    openImpl(value, "_blank", "noopener,noreferrer");
  } else if (
    typeof window !== "undefined" &&
    typeof window.open === "function"
  ) {
    window.open(value, "_blank", "noopener,noreferrer");
  } else {
    return false;
  }

  return true;
}
